import './../products/products.scss'
import { PlusOutlined, UserOutlined, WhatsAppOutlined, DollarOutlined, CalendarOutlined, DeleteOutlined, CreditCardOutlined, ClockCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { Button, Space, Popover, Popconfirm, Tag, Modal, Tabs } from 'antd';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from '../../config';
import moment from 'moment';
import { toast } from 'react-toastify';
import FormPaiement from './formPaiement/FormPaiement';
import PaiementJour from './PaiementJour';
import PaiementJour7 from './PaiementJour7';
import PaiementJour30 from './PaiementJour30';
import canSeeFinance from '../../utils/canSeeFinance';
import {
    NdoePage, NdoePageHeader, NdoeToolbar, NdoeActionButton,
    NdoeResponsiveTable, NdoeStats, NdoeStatCard, NdoeDataCard,
    NdoeRecherche, filtreTexte, optionsDe, compterActifs,
} from '../../components/ndoe';

const Paiement = () => {
    const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [open, setOpen] = useState(false);
    // Recherche unifiée : la période recharge côté serveur, le reste filtre
    // ce qui est déjà à l'écran.
    const [moyen, setMoyen] = useState(undefined);
    const [debut, setDebut] = useState('');
    const [fin, setFin] = useState('');
    const [chargePeriode, setChargePeriode] = useState(false);
    const user = useSelector((state) => state.user?.currentUser);
    const isAdmin = user?.role === 'admin';
    const showFinance = canSeeFinance(user);

    // Fetch réutilisable (remplace le useEffect inline et permet le refetch
    // après mutation à la place d'un window.location.reload()).
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/vente/vente/paiement`);
        setData(data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        toast.error('Erreur lors du chargement des paiements');
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DOMAIN]);

    const handleDelete = async (id) => {
      try {
        await axios.delete(`${DOMAIN}/api/vente/vente/paiement/${id}`);
        toast.success('Paiement supprimé');
        fetchData();   // refetch au lieu de recharger toute la page
      } catch (err) {
        toast.error('Erreur lors de la suppression');
      }
    };

    const columns = [
        { title: '#', dataIndex: 'id', key: 'id', render: (text, record, index) => index + 1, width:"3%"},
        {
            title: 'Client',
            dataIndex: 'nom',
            key: 'nom',
            render: (text, record) => (
              <div>
                <Tag color="green">
                  <Space>
                    <UserOutlined />
                    {text}
                  </Space>
                </Tag>
              </div>
            )
          },
          {
            title: 'Telephone',
            dataIndex: 'telephone',
            key: 'email',
            render: (text) => (
              <Popover content="Discutez avec lui sur WhatsApp" placement="top">
              <Tag
                onClick={() => window.open(`https://wa.me/${text}`, '_blank')}
                color="green"
                style={{ cursor: 'pointer' }}
              >
                <WhatsAppOutlined style={{ color: 'green', marginRight: '5px' }} />
                {text}
              </Tag>
            </Popover>
            ),
          },
          {
            title: 'Date de paiement',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: (a, b) => moment(a.created_at) - moment(b.created_at),
            sortDirections: ['descend', 'ascend'],
            render: (text) => (
              <Tag icon={<CalendarOutlined />} color="blue">
                {moment(text).format('DD-MM-yyyy')}
              </Tag>
            ),
          },
          {
            title: 'Montant payé',
            dataIndex: 'montant',
            key: 'montant',
            sorter: (a, b) => a.montant - b.montant,
            sortDirections: ['descend', 'ascend'],
            render: (text, record) => (
              <Tag color="green" icon={<DollarOutlined />}>
                {record.montant.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </Tag>
            ),
          },
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => (

              <Space size="middle">
                {user?.role === 'admin' &&
                <Popover title="Supprimer" trigger="hover">
                  <Popconfirm
                    title="Êtes-vous sûr de vouloir supprimer?"
                    onConfirm={() => handleDelete(record.id_paiement)}
                    okText="Oui"
                    cancelText="Non"
                  >
                    <Button icon={<DeleteOutlined />} style={{ color: 'red' }} />
                  </Popconfirm>
                </Popover>}
              </Space>
            ),
          },
      ];

  // Zone libre : tous les critères affichés, plusieurs mots = ET. Avant, elle
  // ne balayait que le nom de la cliente. Le MONTANT est volontairement absent :
  // il vit derrière canSeeFinance, le rendre cherchable l'exposerait.
  const filteredData = (data || [])
    .filter(filtreTexte(searchValue, ['nom', 'telephone', 'moyen', 'reference']))
    .filter((p) => !moyen || String(p.moyen) === moyen);

  // Période : rechargement serveur (mêmes bornes que l'ancien panneau).
  const chercherPeriode = async () => {
    setChargePeriode(true);
    try {
      const url = debut && fin
        ? `${DOMAIN}/api/vente/vente/paiement?start_date=${debut}&end_date=${fin}`
        : `${DOMAIN}/api/vente/vente/paiement`;
      const { data: rows } = await axios.get(url);
      setData(rows);
    } catch (error) {
      toast.error('Impossible de charger les paiements sur cette période.');
    } finally {
      setChargePeriode(false);
    }
  };

  // KPI (montant total derrière canSeeFinance).
  const totalMontant = (data || []).reduce((sum, it) => sum + (Number(it.montant) || 0), 0);

  // Carte "ligne" mobile (remplace une ligne de tableau). La métrique montant
  // reste DERRIÈRE canSeeFinance, comme la colonne desktop.
  const renderPaiementCard = (record) => {
    const phone = record.telephone;
    const actions = [
      phone && (
        <NdoeActionButton
          key="w"
          size="sm"
          variant="secondary"
          icon={<WhatsAppOutlined />}
          as="a"
          href={`https://wa.me/${phone}`}
          target="_blank"
          rel="noreferrer"
        >
          WhatsApp
        </NdoeActionButton>
      ),
      isAdmin && (
        <Popconfirm
          key="d"
          title="Êtes-vous sûr de vouloir supprimer?"
          onConfirm={() => handleDelete(record.id_paiement)}
          okText="Oui"
          cancelText="Non"
        >
          <NdoeActionButton size="sm" variant="danger" icon={<DeleteOutlined />}>Suppr.</NdoeActionButton>
        </Popconfirm>
      ),
    ].filter(Boolean);

    return (
      <NdoeDataCard
        title={record.nom}
        subtitle={`${record.telephone || ''}${record.created_at ? ' · ' + moment(record.created_at).format('DD-MM-yyyy') : ''}`}
        metrics={showFinance ? [{
          value: record.montant?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
          label: 'montant payé',
        }] : []}
        actions={actions}
      />
    );
  };

  // ----- Onglet "Paiements" -----
  const tabAll = (
    <>
      {/* L'ancien bouton « Filtres avancés » (PaiementSelect) ne proposait
          qu'une plage de dates : elle est désormais dans la barre elle-même. */}
      <NdoeRecherche
        q={searchValue}
        onQ={setSearchValue}
        placeholder="Cliente, téléphone, moyen de paiement…"
        periode={{ debut, fin }}
        onPeriode={(d, f) => { setDebut(d); setFin(f); }}
        onChercher={chercherPeriode}
        chargement={chargePeriode}
        facettes={[{
          cle: 'moyen',
          placeholder: 'Moyen',
          options: optionsDe(data, 'moyen'),
          valeur: moyen,
          onChange: setMoyen,
          largeur: 150,
        }]}
        actifs={compterActifs(searchValue, moyen)}
        onEffacer={() => { setSearchValue(''); setMoyen(undefined); }}
        resume={`${filteredData.length} sur ${data?.length || 0} paiement${(data?.length || 0) > 1 ? 's' : ''}`}
      />

      <NdoeResponsiveTable
        data={filteredData}
        columns={columns.filter((col) => showFinance || col.key !== 'montant')}
        rowKey="id_paiement"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10 }}
        renderCard={renderPaiementCard}
        empty={{ icon: '💳', title: 'Aucun paiement', text: 'Ajustez la recherche ou les filtres.' }}
      />
    </>
  );

  const tabItems = [
    { key: '0', label: <span><CreditCardOutlined />Paiements</span>, children: tabAll },
    { key: '1', label: <span><CalendarOutlined />Paiements du jour</span>, children: <PaiementJour /> },
    { key: '2', label: <span><ClockCircleOutlined />7 derniers jours</span>, children: <PaiementJour7 /> },
    { key: '3', label: <span><ClockCircleOutlined />30 derniers jours</span>, children: <PaiementJour30 /> },
  ];

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Finance"
        title="Liste des paiements"
        subtitle="Gérez et suivez vos paiements"
        actions={
          <NdoeActionButton variant="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            Ajouter un paiement
          </NdoeActionButton>
        }
      />

      <NdoeStats>
        <NdoeStatCard label="Paiements" value={data?.length || 0} />
        {showFinance && (
          <NdoeStatCard gold label="Montant total" value={<>{totalMontant.toLocaleString('en-US')} $</>} />
        )}
      </NdoeStats>

      <Tabs type="card" items={tabItems} />

      {/* Ajout d'un paiement */}
      <Modal
        centered
        title="Ajouter un paiement"
        open={open}
        onCancel={() => setOpen(false)}
        width={650}
        footer={[]}
      >
        <FormPaiement fetchData={fetchData} closeModal={() => setOpen(false)} />
      </Modal>
    </NdoePage>
  )
}

export default Paiement
